/**
 * Operational endpoint — a submission's staffing-ratio rows.
 *
 * Wired to the real Orion v2 backend (`/v2/staffing-ratios/`, under the
 * Facility group). These bypass the mock layer via the `*Real` helpers,
 * same as the v2 submissions endpoints.
 *
 *   GET    /v2/staffing-ratios/?submission_id=:id   → { total, items[] }
 *   POST   /v2/staffing-ratios/                     body: StaffingRatioCreate
 *   PATCH  /v2/staffing-ratios/:ratioId             body: StaffingRatioUpdate
 *   DELETE /v2/staffing-ratios/:ratioId
 *
 * Backend now exposes RN + LPN + CNA (staffing + state_avg) plus a
 * derived `total_staffing` / `total_state_avg` and `status`. The three
 * inputs (rn/lpn/cna staffing) are writable; Total is summed server-side
 * (read-only), and `*_state_avg` / `status` are derived/read-only too —
 * they come back null until the backend populates them (data, not a gap).
 * Writes carry facility_id, year, rn_staffing, lpn_staffing, cna_staffing.
 */
import type { StaffingRatio, StaffingRatioWritePayload } from "../../types/operational";
import { getJsonReal, sendJsonReal } from "../client";

const STAFFING_BASE = "/v2/staffing-ratios";

// ──────────────────────── Wire shapes (snake_case) ────────────────────────

interface StaffingRatioWire {
    id: string;
    facility_id: string;
    year: number;
    rn_staffing: string | null;
    rn_state_avg: string | null;
    lpn_staffing: string | null;
    lpn_state_avg: string | null;
    cna_staffing: string | null;
    cna_state_avg: string | null;
    total_staffing: string | null;
    total_state_avg: string | null;
    status: string | null;
    created_at: string;
    updated_at: string;
}

/** Item shape returned by the submission-scoped list — carries the
 *  facility details inline. The backend returns the row id as `id`
 *  (older docs/spec said `ratio_id`); we read `id` and keep `ratio_id`
 *  as a tolerant fallback. Getting this wrong left every row id
 *  `undefined`, so PATCH/DELETE hit `/staffing-ratios/undefined` and
 *  silently failed (edits/deletes never persisted). */
interface StaffingRatioSubmissionItemWire {
    id?: string;
    ratio_id?: string;
    facility_id: string;
    facility_name: string;
    address: string | null;
    city: string | null;
    state: string | null;
    year: number;
    rn_staffing: string | null;
    rn_state_avg: string | null;
    lpn_staffing: string | null;
    lpn_state_avg: string | null;
    cna_staffing: string | null;
    cna_state_avg: string | null;
    total_staffing: string | null;
    total_state_avg: string | null;
    status: string | null;
}

interface StaffingRatioSubmissionResponseWire {
    total: number;
    items: StaffingRatioSubmissionItemWire[];
}

// ──────────────────────────── Adapters ────────────────────────────

const str = (v: string | null | undefined): string => v ?? "";

/** Map a wire row (either response shape) to the FE model. Total +
 *  *_state_avg + status are server-derived (read-only); they may be null
 *  until the backend populates them. */
function toModel(
    submissionId: string,
    w: {
        id: string;
        facility_id: string;
        facility_name?: string | null;
        address?: string | null;
        city?: string | null;
        state?: string | null;
        year: number;
        rn_staffing: string | null;
        rn_state_avg: string | null;
        lpn_staffing: string | null;
        lpn_state_avg: string | null;
        cna_staffing: string | null;
        cna_state_avg: string | null;
        total_staffing: string | null;
        total_state_avg: string | null;
        created_at?: string;
        updated_at?: string;
    },
): StaffingRatio {
    return {
        id: w.id,
        submissionId,
        facilityId: w.facility_id,
        facilityName: str(w.facility_name),
        address: str(w.address),
        city: str(w.city),
        state: str(w.state),
        year: w.year,
        rnStaffing: str(w.rn_staffing),
        rnStAvg: str(w.rn_state_avg),
        lpnStaffing: str(w.lpn_staffing),
        lpnStAvg: str(w.lpn_state_avg),
        cnaStaffing: str(w.cna_staffing),
        cnaStAvg: str(w.cna_state_avg),
        totalStaffing: str(w.total_staffing),
        totalStAvg: str(w.total_state_avg),
        createdAt: str(w.created_at),
        updatedAt: str(w.updated_at),
    };
}

/** POST body — only the fields the backend accepts on create. The three
 *  inputs (rn/lpn/cna) are writable; Total + state averages are derived. */
function toCreateBody(p: StaffingRatioWritePayload): Record<string, unknown> {
    const body: Record<string, unknown> = {
        facility_id: p.facilityId ?? "",
        year: p.year ?? 0,
    };
    if (p.rnStaffing) body.rn_staffing = p.rnStaffing;
    if (p.lpnStaffing) body.lpn_staffing = p.lpnStaffing;
    if (p.cnaStaffing) body.cna_staffing = p.cnaStaffing;
    return body;
}

/** PATCH body — only the writable inputs present in the staged diff.
 *  Total + state-average edits are dropped (server-derived). */
function toUpdateBody(p: StaffingRatioWritePayload): Record<string, unknown> {
    const body: Record<string, unknown> = {};
    if (p.year !== undefined) body.year = p.year;
    if (p.rnStaffing !== undefined) body.rn_staffing = p.rnStaffing === "" ? null : p.rnStaffing;
    if (p.lpnStaffing !== undefined) body.lpn_staffing = p.lpnStaffing === "" ? null : p.lpnStaffing;
    if (p.cnaStaffing !== undefined) body.cna_staffing = p.cnaStaffing === "" ? null : p.cnaStaffing;
    return body;
}

// ──────────────────────────── Endpoints ────────────────────────────

export function listStaffingRatios(submissionId: string): Promise<StaffingRatio[]> {
    const scope = submissionId.trim();
    return getJsonReal<StaffingRatioSubmissionResponseWire>(
        `${STAFFING_BASE}/?submission_id=${encodeURIComponent(scope)}`,
    ).then((res) => res.items.map((it) => toModel(scope, { ...it, id: it.id ?? it.ratio_id ?? "" })));
}

export function createStaffingRatio(
    submissionId: string,
    payload: StaffingRatioWritePayload,
): Promise<StaffingRatio> {
    return sendJsonReal<StaffingRatioWire>(`${STAFFING_BASE}/`, "POST", toCreateBody(payload)).then((w) =>
        toModel(submissionId.trim(), w),
    );
}

export function patchStaffingRatio(
    submissionId: string,
    rowId: string,
    payload: StaffingRatioWritePayload,
): Promise<StaffingRatio> {
    return sendJsonReal<StaffingRatioWire>(
        `${STAFFING_BASE}/${encodeURIComponent(rowId)}`,
        "PATCH",
        toUpdateBody(payload),
    ).then((w) => toModel(submissionId.trim(), w));
}

export function deleteStaffingRatio(_submissionId: string, rowId: string): Promise<void> {
    return sendJsonReal<void>(`${STAFFING_BASE}/${encodeURIComponent(rowId)}`, "DELETE");
}
